/**
 * O primeiro dia da semana da grade, guardado no navegador de quem olha.
 *
 * Papel: aqui fica `usePrimeiroDia`, o estado do "Domingo primeiro / Segunda
 * primeiro" da barra, lido e gravado em `localStorage`.
 *
 * Como usar: a escolha é de cada navegador, e não da casa — é como cada um lê a
 * grade. Sem `localStorage` (janela anônima, armazenamento bloqueado), a grade
 * abre na segunda-feira e a troca vale até a página fechar.
 */
import { useState } from 'react';

import { type PrimeiroDia } from '../grade';

/** A chave em `localStorage`. */
const CHAVE = 'concierge.calendario.primeiro-dia';

/** O primeiro dia quando nada foi escolhido: segunda, como o arquivo de origem. */
const PADRAO: PrimeiroDia = 1;

/** Lê a escolha guardada, ou a segunda quando não há nenhuma legível. */
function lerGuardado(): PrimeiroDia {
  try {
    const guardado = window.localStorage.getItem(CHAVE);
    return guardado === '0' ? 0 : guardado === '1' ? 1 : PADRAO;
  } catch {
    return PADRAO;
  }
}

/**
 * O primeiro dia da semana e a função que o troca.
 *
 * @returns O dia em que a semana começa e a função que grava a troca.
 */
export function usePrimeiroDia(): [PrimeiroDia, (dia: PrimeiroDia) => void] {
  const [primeiroDia, setPrimeiroDia] = useState<PrimeiroDia>(lerGuardado);

  /** Troca o primeiro dia na tela e tenta guardá-lo no navegador. */
  function handleTrocar(dia: PrimeiroDia) {
    setPrimeiroDia(dia);
    try {
      window.localStorage.setItem(CHAVE, String(dia));
    } catch {
      // Sem armazenamento, a escolha vale só até a página fechar.
    }
  }

  return [primeiroDia, handleTrocar];
}
